import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import EmailMarketing from '../components/EmailMarketing';
import blogPosts from '../data/blogPosts';
import { FaSearch } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

const categories = [
  "All",
  "Travel Hotspots", 
  "Travel Safety",
  "Health & Safety",
  "Best Times to Visit",
  "Adventure Travel",
  "Travel Insurance",
  "Activity holidays"
];

const tags = [
  "Mountain Climbing",
  "Hiking",
  "Beach",
  "City Breaks",
  "Cultural",
  "Adventure",
  "Solo Travel",
  "Group Travel",
  "Budget",
  "Luxury",
  "Food & Dining",
  "Photography",
  "History",
  "Nature",
  "Water Sports",
  "Winter Sports",
  "Cycling",
  "Rock Climbing",
  "Skiing",
  "Snowboarding",
  "Kayaking",
  "Rafting",
  "Paragliding",
  "Canyoning"
];

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [sortBy, setSortBy] = useState("date"); // date, title, readTime

  // Enhanced search with multiple criteria
  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      // Category filter
      const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
      
      // Text search (title, excerpt, content, author)
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === "" || 
        post.title.toLowerCase().includes(searchLower) ||
        post.excerpt.toLowerCase().includes(searchLower) ||
        post.content.toLowerCase().includes(searchLower) ||
        post.author.toLowerCase().includes(searchLower);
      
      // Tag filter
      const matchesTags = selectedTags.length === 0 || 
        selectedTags.some(tag => 
          (post.tags && post.tags.some(postTag => postTag.toLowerCase().includes(tag.toLowerCase()))) ||
          post.title.toLowerCase().includes(tag.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(tag.toLowerCase())
        );
      
      return matchesCategory && matchesSearch && matchesTags;
    }).sort((a, b) => {
      switch (sortBy) {
        case "title":
          return a.title.localeCompare(b.title);
        case "readTime":
          return parseInt(a.readTime) - parseInt(b.readTime);
        case "date":
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });
  }, [selectedCategory, searchTerm, selectedTags, sortBy]);

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSelectedCategory("All");
    setSearchTerm("");
    setSelectedTags([]);
    setSortBy("date");
  };

  const hasActiveFilters = selectedCategory !== "All" || searchTerm !== "" || selectedTags.length > 0;

  // Handle image errors
  const handleImageError = (e) => {
    e.target.src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=400&fit=crop&q=80';
    e.target.alt = 'Travel destination';
  };

  return (
    <div>
      <Helmet>
        <title>Travel Blog & Stories | GradedWash</title>
        <meta name="description" content="Discover travel tips, safety advice, destination guides, and inspiring stories from fellow travelers around the world. Expert insights on adventure travel, budget tips, and hidden gems." />
        <meta name="keywords" content="travel blog, travel tips, adventure travel, destination guides, travel safety, budget travel, solo travel, travel stories" />
        <meta property="og:title" content="Travel Blog & Stories | GradedWash" />
        <meta property="og:description" content="Discover travel tips, safety advice, destination guides, and inspiring stories from fellow travelers around the world." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gradedwash.com/blog" />
        <link rel="canonical" href="https://gradedwash.com/blog" />
      </Helmet>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%)',
        color: 'white',
        padding: 'var(--spacing-16) 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{ fontSize: 'var(--font-size-4xl)', marginBottom: 'var(--spacing-6)' }}>
            Travel Blog & Stories
          </h1>
          <p style={{ 
            fontSize: 'var(--font-size-xl)', 
            maxWidth: '600px',
            margin: '0 auto',
            color: 'white',
            fontWeight: '500',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
          }}>
            Discover travel tips, safety advice, destination guides, and inspiring stories 
            from fellow travelers around the world.
          </p>
        </div>
      </section>

      {/* Enhanced Search and Filter Section */}
      <section className="bg-light p-6" style={{
        background: 'linear-gradient(135deg, #e3f0ff 0%, #f8fbff 100%)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        borderRadius: 'var(--radius-lg)',
        marginTop: '-3em',
        marginBottom: '2em',
        position: 'relative',
        zIndex: 2
      }}>
        <div className="container">
          {/* Main Search Bar */}
          <div className="row align-center justify-between" style={{ marginBottom: 'var(--spacing-4)' }}>
            <div className="col col--8">
              <div className="form-group" style={{ margin: 0, position: 'relative' }}>
                <FaSearch style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#b0b8c1', fontSize: '1.2em' }} />
                <input
                  type="text"
                  placeholder="Search articles by title, content, author, or keywords (e.g., 'mountain climbing', 'beach', 'solo travel')..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input"
                  style={{ width: '100%', padding: 'var(--spacing-3) var(--spacing-3) var(--spacing-3) 2.5em', fontSize: '1.15em', borderRadius: 'var(--radius-md)', border: '1.5px solid #b0b8c1', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}
                />
              </div>
            </div>
            <div className="col col--4">
              <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                  className="btn btn--secondary"
                  style={{ fontWeight: 600, fontSize: '1.05em', borderRadius: 'var(--radius-md)' }}
                >
                  {showAdvancedSearch ? 'Hide' : 'Advanced'} Search
                </button>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="btn btn--secondary"
                    style={{ background: 'var(--error-red)', color: 'white', fontWeight: 600, borderRadius: 'var(--radius-md)' }}
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Advanced Search Options */}
          {showAdvancedSearch && (
            <div style={{ 
              background: 'white', 
              padding: 'var(--spacing-4)', 
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--spacing-4)',
              border: '1px solid var(--gray-200)'
            }}>
              <h4 style={{ marginBottom: 'var(--spacing-3)' }}>Advanced Search Options</h4>
              
              {/* Categories */}
              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <h5 style={{ marginBottom: 'var(--spacing-2)' }}>Categories:</h5>
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`btn btn--small ${selectedCategory === category ? 'btn--primary' : 'btn--secondary'}`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <h5 style={{ marginBottom: 'var(--spacing-2)' }}>Popular Topics:</h5>
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                  {tags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => handleTagToggle(tag)}
                      className={`btn btn--small ${selectedTags.includes(tag) ? 'btn--primary' : 'btn--secondary'}`}
                      style={{ 
                        background: selectedTags.includes(tag) ? 'var(--primary-blue)' : 'var(--gray-200)',
                        color: selectedTags.includes(tag) ? 'white' : 'var(--gray-700)'
                      }}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort Options */}
              <div>
                <h5 style={{ marginBottom: 'var(--spacing-2)' }}>Sort By:</h5>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    padding: 'var(--spacing-2)',
                    border: '1px solid var(--gray-300)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '1em'
                  }}
                >
                  <option value="date">Newest First</option>
                  <option value="title">Alphabetical</option>
                  <option value="readTime">Reading Time</option>
                </select>
              </div>
            </div>
          )}

          {/* Quick Category Filter */}
          {!showAdvancedSearch && (
            <div className="row">
              <div className="col col--12">
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`btn btn--small ${selectedCategory === category ? 'btn--primary' : 'btn--secondary'}`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Search Results Summary */}
      {hasActiveFilters && (
        <section className="p-4" style={{ background: 'var(--gray-50)' }}>
          <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>Search Results:</strong> {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found
                {searchTerm && <span> for "{searchTerm}"</span>}
                {selectedCategory !== "All" && <span> in {selectedCategory}</span>}
                {selectedTags.length > 0 && <span> tagged with {selectedTags.join(', ')}</span>}
              </div>
              <button
                onClick={clearFilters}
                className="btn btn--secondary btn--small"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Featured Article */}
      {filteredPosts.length > 0 && (
        <section className="p-6">
          <div className="container">
            <h2 className="m-6">Featured Article</h2>
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="row">
                <div className="col col--6">
                  <img 
                    src={filteredPosts[0].image}
                    alt={filteredPosts[0].title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={handleImageError}
                  />
                </div>
                <div className="col col--6">
                  <div className="card__body p-6">
                    <div style={{ marginBottom: 'var(--spacing-3)' }}>
                      <span className="bg-primary text-white" style={{
                        padding: 'var(--spacing-1) var(--spacing-2)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'bold'
                      }}>
                        {filteredPosts[0].category}
                      </span>
                      <span className="text-muted" style={{ marginLeft: 'var(--spacing-2)' }}>
                        {filteredPosts[0].readTime}
                      </span>
                    </div>
                    <h3 style={{ marginBottom: 'var(--spacing-3)' }}>{filteredPosts[0].title}</h3>
                    <p style={{ marginBottom: 'var(--spacing-4)', lineHeight: 1.6 }}>
                      {filteredPosts[0].excerpt}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="text-muted">By {filteredPosts[0].author} • {new Date(filteredPosts[0].date).toLocaleDateString()}</span>
                      <Link to={`/blog/${filteredPosts[0].id}`} className="btn btn--primary">
                        Read More
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="p-6">
        <div className="container">
          {filteredPosts.length > 0 ? (
            <>
              <h2 className="m-6">All Articles</h2>
              <div className="row" style={{ gap: 'var(--spacing-4)' }}>
                {filteredPosts.slice(1).map(post => (
                  <div key={post.id} className="col col--4">
                    <div className="card" style={{ height: '100%' }}>
                      <img 
                        src={post.image}
                        alt={post.title}
                        style={{ 
                          width: '100%', 
                          height: '200px', 
                          objectFit: 'cover',
                          borderTopLeftRadius: 'var(--radius-md)',
                          borderTopRightRadius: 'var(--radius-md)'
                        }}
                        onError={handleImageError}
                      />
                      <div className="card__body p-4">
                        <div style={{ marginBottom: 'var(--spacing-2)' }}>
                          <span className="bg-primary text-white" style={{
                            padding: 'var(--spacing-1) var(--spacing-2)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'bold'
                          }}>
                            {post.category}
                          </span>
                          <span className="text-muted" style={{ marginLeft: 'var(--spacing-2)' }}>
                            {post.readTime}
                          </span>
                        </div>
                        <h4 style={{ marginBottom: 'var(--spacing-2)' }}>{post.title}</h4>
                        <p style={{ 
                          marginBottom: 'var(--spacing-3)', 
                          lineHeight: 1.5,
                          color: 'var(--gray-600)'
                        }}>
                          {post.excerpt}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>
                            By {post.author} • {new Date(post.date).toLocaleDateString()}
                          </span>
                          <Link to={`/blog/${post.id}`} className="btn btn--secondary btn--small">
                            Read More
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-8)' }}>
              <div style={{ fontSize: '3em', marginBottom: 'var(--spacing-4)' }}>🔍</div>
              <h3>No articles found</h3>
              <p style={{ color: 'var(--gray-600)', marginBottom: 'var(--spacing-4)' }}>
                Try adjusting your search terms or filters to find what you're looking for.
              </p>
              <button
                onClick={clearFilters}
                className="btn btn--primary"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Email Marketing Section */}
      <EmailMarketing />
    </div>
  );
} 